<template>
  <section
    class="training-section-grid-block-item"
    :class="{'is-bg': bgImage, 'is-dark': isDark}"
  >
    <BgMedia
      v-if="bgImage"
      :media="getBackground()"
    />
    <div class="content">
      <h3
        v-if="title"
        class="grid-block-title"
        v-html="title"
      ></h3>
      <div
        v-if="text"
        class="grid-block-text"
        v-html="markdownToHTML(text || '')"
      ></div>
    </div>
  </section>
</template>

<script>
import BgMedia from '@/components/BgMedia.vue';
import getUrl from '@/tools/getUrl';

// Markdown
const md = require('markdown-it')({
  html: true,
  linkify: true,
  typographer: true,
});

export default {
  name: 'TrainingSectionGridBlockItem',
  components: {
    BgMedia,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    background: {
      type: Object,
      default: null,
    },
    backgroundMobile: {
      type: Object,
      default: null,
    },
    theme: {
      type: String,
      default: 'light',
      validator: (value) => ['light', 'dark', 'backgroundImage'].includes(value),
    },
  },
  computed: {
    bgImage() {
      return this.theme === 'backgroundImage';
    },
    isDark() {
      return this.theme === 'dark';
    },
  },
  methods: {
    markdownToHTML(content) {
      return md.render(content);
    },
    getUrl,
    getBackground() {
      return {
        background: this.background,
        backgroundMobile: this.backgroundMobile,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.training-section-grid-block-item {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  overflow: hidden;
  min-height: 400px;
  padding: 60px 40px;
  box-sizing: border-box;

  @include screen("sm", max) {
    min-height: 250px;
    padding: 100px 30px;
  }

  ::v-deep(.bg-media) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
  }

  .content {
    position: relative;
    z-index: 2;
    width: 100%;

    @include wRule(
      padding-left,
      (
        xl: 12%,
        lg: 10%,
        md: 8%,
        sm: 0%,
      )
    );

    @include wRule(
      max-width,
      (
        xl: 58%,
        lg: 60%,
        md: 70%,
        sm: 100%,
      )
    );
  }

  .grid-block-title {
    position: relative;
    font-weight: 700;
    line-height: 1.2;
    text-transform: uppercase;

    @include wRule(
      font-size,
      (
        xl: 32,
        lg: 28,
        md: 24,
        sm: 36,
      )
    );

    &:not(:last-child) {
      @include wRule(
        margin-bottom,
        (
          xl: 24,
          lg: 20,
          md: 18,
          sm: 24,
        )
      );
    }

    &::after {
      content: '';
      display: block;
      height: 3px;
      background-color: #2a5388;

      width: 66.666%;

      @include wRule(
        margin-top,
        (
          xl: 16,
          lg: 14,
          md: 12,
          sm: 20,
        )
      );
    }
  }

  .grid-block-text {
    font-weight: 400;
    line-height: 1.6;

    @include wRule(
      font-size,
      (
        xl: 18,
        lg: 16,
        md: 14,
        sm: 28,
      )
    );

    ::v-deep(p) {
      margin: 0;

      &:not(:last-child) {
        @include wRule(
          margin-bottom,
          (
            xl: 16,
            lg: 14,
            md: 12,
            sm: 20,
          )
        );
      }
    }
  }

  &.is-bg {
    background-color: color(dark);
    color: color(basic-light);

    .content {
      padding: 40px 30px;
      margin: -60px -40px;

      @include screen("sm", max) {
        padding: 30px 20px;
        margin: -40px -30px;
      }

      @include screen("md", max) {
        backdrop-filter: none;
      }
    }
  }

  &.is-dark {
    background-color: color(dark);
    color: color(basic-light);
  }
}
</style>
